import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import FloatingActions from '@/pages/home/components/FloatingActions';
import { useScrollAnimation } from '@/hooks/useScroll';
import {
  companyInfo,
  leadership,
  manufacturingStats,
  aboutTimeline,
} from '@/mocks/homeData';

function CountUpNumber({ end }: { end: number }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    let frame: number;
    const duration = 2000;
    const animate = (ts: number) => {
      if (!startTime) startTime = ts;
      const p = Math.min((ts - startTime) / duration, 1);
      setCount(Math.floor(p * end));
      if (p < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [started, end]);

  return <span ref={ref}>{count}</span>;
}

function FormattedText({ text }: { text: string }) {
  const paragraphs = text.split('\n\n').filter(Boolean);

  return (
    <>
      {paragraphs.map((paragraph, index) => {
        const parts = paragraph.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);

        return (
          <p key={index} className="text-foreground-600 leading-relaxed mb-4 last:mb-0">
            {parts.map((part, partIndex) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return (
                  <strong key={`${index}-${partIndex}`} className="font-semibold text-foreground-800">
                    {part.slice(2, -2)}
                  </strong>
                );
              }

              return <span key={`${index}-${partIndex}`}>{part}</span>;
            })}
          </p>
        );
      })}
    </>
  );
}

export default function AboutPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[420px] md:h-[520px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Modern%20textile%20factory%20exterior%20with%20clean%20architecture%2C%20glass%20facade%20reflecting%20blue%20sky%2C%20landscaped%20surroundings%2C%20bright%20natural%20daylight%2C%20professional%20corporate%20building%20photography%2C%20warm%20inviting%20atmosphere%2C%20sustainable%20industrial%20design&width=1600&height=800&seq=about-hero-01&orientation=landscape)',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 text-center">
          <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-white/80 mb-4">
            A Beaumonde Tex Facility
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            About BA Fashionwear
          </h1>
          <p className="max-w-[600px] mx-auto text-base md:text-lg text-white/80 leading-relaxed">
            Discover the story behind one of Bangladesh's most trusted apparel manufacturers.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <div className="lg:w-1/2 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <div className="relative overflow-hidden rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20factory%20interior%20photography%20of%20modern%20textile%20manufacturing%20facility%2C%20bright%20clean%20production%20floor%20with%20organized%20workstations%2C%20natural%20and%20artificial%20lighting%2C%20warm%20industrial%20atmosphere%2C%20workers%20at%20sewing%20stations%2C%20high%20quality%20corporate%20documentary%20style&width=800&height=600&seq=about-story-01&orientation=landscape"
                  alt="BA Fashionwear Factory Interior"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
            <div className="lg:w-1/2 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200">
              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">
                Our Story
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6 leading-tight">
                From Vision to Reality
              </h2>
              <div className="text-foreground-600 leading-relaxed mb-5">
                <FormattedText text={companyInfo.history} />
              </div>
              <div className="grid grid-cols-3 gap-6 pt-4 border-t border-[#E6EAF0]">
                <div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500">
                    {new Date().getFullYear() - 2021}+
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">Years of Excellence</div>
                </div>
                <div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500">
                    28
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">Export Markets</div>
                </div>
                <div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500">
                    45
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">Production Lines</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-8 md:p-10 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-50 mb-6">
                <i className="ri-focus-3-line text-xl text-primary-500"></i>
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                Our Mission
              </h3>
              <p className="text-foreground-600 leading-relaxed">
                {companyInfo.mission}
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 md:p-10 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200">
              <div className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-50 mb-6">
                <i className="ri-eye-line text-xl text-accent-500"></i>
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                Our Vision
              </h3>
              <p className="text-foreground-600 leading-relaxed">
                {companyInfo.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">
              What We Stand For
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              Our Core Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {companyInfo.values.map((v, i) => (
              <div
                key={v.title}
                className="bg-white rounded-lg p-7 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 border border-[#E6EAF0]"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-50 mb-5">
                  <i className="ri-check-double-line text-lg text-primary-500"></i>
                </div>
                <h4 className="font-heading text-base font-semibold text-foreground-900 mb-3">
                  {v.title}
                </h4>
                <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Journey - Timeline */}
      {/* Our Journey - Timeline */}
      {/* <section className="py-16 md:py-24 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">
              Milestones
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              Our Journey
            </h2>
            <p className="mt-3 text-foreground-500 max-w-[650px] mx-auto text-sm md:text-base leading-relaxed">
              A timeline of the milestones that shaped BA Fashionwear into a trusted global manufacturing partner.
            </p>
          </div>

          <div className="relative mt-8 md:mt-12">
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-primary-200"></div>

            <div className="space-y-8 md:space-y-12">
              {aboutTimeline.map((item, i) => {
                const isEven = i % 2 === 0;

                return (
                  <div
                    key={item.year}
                    className="relative flex items-center justify-center animate-on-scroll opacity-0 translate-y-8 transition-all duration-700"
                    style={{ transitionDelay: `${i * 80}ms` }}
                  >
                    <div className="w-full grid grid-cols-2 gap-4 md:gap-12 items-center">
                      
                      <div className={`flex ${isEven ? 'justify-end' : 'opacity-0 pointer-events-none hidden sm:flex'}`}>
                        {isEven && (
                          <div className="w-full max-w-[420px] rounded-2xl border border-[#E6EAF0] bg-white p-5 md:p-6 shadow-sm text-right">
                            <span className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-600 font-heading mb-3">
                              {item.year}
                            </span>
                            <h4 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2">
                              {item.title}
                            </h4>
                            <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className={`flex ${!isEven ? 'justify-start' : 'opacity-0 pointer-events-none hidden sm:flex'}`}>
                        {!isEven && (
                          <div className="w-full max-w-[420px] rounded-2xl border border-[#E6EAF0] bg-white p-5 md:p-6 shadow-sm text-left">
                            <span className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-600 font-heading mb-3">
                              {item.year}
                            </span>
                            <h4 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2">
                              {item.title}
                            </h4>
                            <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        )}
                      </div>

                    </div>

                    <div className="absolute left-1/2 -translate-x-1/2 w-8 h-8 md:w-[38px] md:h-[38px] rounded-full bg-primary-500 border-4 border-background-100 flex items-center justify-center z-10 shadow-sm">
                      <div className="w-2 md:w-2.5 h-2 md:h-2.5 rounded-full bg-white"></div>
                    </div>

                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section> */}

      {/* Factory at a Glance */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">
              Infrastructure
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              Factory at a Glance
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {manufacturingStats.slice(0, 8).map((stat, i) => (
              <div
                key={stat.label}
                className="bg-white rounded-lg p-6 text-center border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-md bg-primary-50 mb-4">
                  <i className={`${stat.icon} text-lg text-primary-500`}></i>
                </div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1">
                  <CountUpNumber end={stat.value} />
                  {stat.suffix}
                </div>
                <div className="text-xs text-foreground-500 leading-snug">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">
              Our People
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              Leadership Team
            </h2>
            <p className="mt-3 text-foreground-500 max-w-[500px] mx-auto text-sm leading-relaxed">
              Guided by experienced industry leaders committed to manufacturing excellence.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {leadership.map((person, i) => (
              <div
                key={person.name}
                className="bg-white rounded-lg overflow-hidden border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="h-[260px] overflow-hidden">
                  <img
                    src={person.image}
                    alt={person.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h4 className="font-heading text-base font-semibold text-foreground-900">
                    {person.name}
                  </h4>
                  <p className="text-xs text-primary-500 font-medium mt-0.5 mb-3">{person.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Textile%20fabric%20swatches%20arranged%20beautifully%20on%20a%20light%20wooden%20table%2C%20soft%20natural%20daylight%20streaming%20from%20window%2C%20creative%20design%20studio%20atmosphere%2C%20warm%20neutral%20tones%2C%20calm%20professional%20aesthetic%2C%20shallow%20depth%20of%20field&width=1600&height=600&seq=about-cta-01&orientation=landscape)',
          }}
        ></div>
        <div className="absolute inset-0 bg-primary-500/85"></div>
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4">
            Ready to Partner With Us?
          </h2>
          <p className="text-white/80 max-w-[550px] mx-auto mb-8 text-sm md:text-base leading-relaxed">
            Let's discuss how BA Fashionwear can bring your apparel vision to life with precision
            manufacturing and reliable delivery.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/#contact"
              className="inline-flex items-center px-8 py-3.5 bg-white text-primary-600 text-sm font-semibold font-heading rounded-md hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
            >
              Send an Inquiry
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <a
              href="/#capabilities"
              className="inline-flex items-center px-8 py-3.5 bg-transparent border-2 border-white/30 text-white text-sm font-semibold font-heading rounded-md hover:bg-white/10 transition-colors duration-200 whitespace-nowrap"
            >
              Explore Capabilities
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}